import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DocumentsCard } from '../components/Documents.tsx';
import { ErrorBox } from '../components/Layout.tsx';
import { orgMatches } from '../lib/orgSearch.ts';
import { api, canEditOrgs, canSeeRestricted, get, isAdmin, isStaff, patch, post, ROLE_LABEL, type Role } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useData } from '../lib/useData.ts';
import type { Department, OrgDetails, ParticipantRow } from './Participants.tsx';
import { ArchivedMatches } from '../components/ArchivedMatches.tsx';
import { CoverSheetTopBar, ScanPilesCard } from './CoverSheets.tsx';

// ---------------------------------------------------------------- departments
export function Departments() {
  const { me } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  const { data, error, reload } = useData<Department[]>(`/api/departments${showArchived ? '?archived=1' : ''}`);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const admin = me && isAdmin(me.role);
  const editor = !!me && canEditOrgs(me.role);   // technicians add and rename too
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await post('/api/departments', { name: name.trim() });
      setName(''); setAdding(false);
      await reload();
    } catch (x) { setErr(x); }
  };
  const rename = async (d: Department) => {
    const n = window.prompt('Organization name', d.name);
    if (!n || n === d.name) return;
    await patch(`/api/departments/${d.id}`, { name: n }).catch(setErr);
    await reload();
  };
  const restore = async (d: Department) => {
    setErr(null);
    try { await post(`/api/departments/${d.id}/restore`); await reload(); } catch (x) { setErr(x); }
  };
  const [filter, setFilter] = useState('');
  const shown = data?.filter((d) => !filter || orgMatches(d.name, filter));   // "3 and 20" finds "Three and Twenty ..."
  const nav = useNavigate();
  return (
    <>
      <div className="spread">
        <h1>{showArchived ? 'Archived organizations' : 'Organizations'}</h1>
        <div className="actions">
          <a className="button-link" href="/api/departments/export.xlsx" title="Every organization with its people, assessments and events (archived on a second sheet)">Download (Excel)</a>
          {editor && !showArchived && !adding && <button onClick={() => setAdding(true)}>New organization</button>}
          <button className="secondary" onClick={() => setShowArchived(!showArchived)}>{showArchived ? 'Back to organizations' : 'Show archived'}</button>
        </div>
      </div>
      <ErrorBox error={error ?? err} />
      {editor && !showArchived && adding && (
        <form className="card row" onSubmit={add}>
          <div><label htmlFor="org-new">New organization</label>
            <input id="org-new" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Travelers Rest Fire Department" required />
            <ArchivedMatches query={name} onRestored={() => { setAdding(false); setName(''); void reload(); }} /></div>
          <div className="actions" style={{ flex: '0 0 auto' }}>
            <button>Add</button>
            <button type="button" className="secondary" onClick={() => { setAdding(false); setName(''); }}>Cancel</button></div>
        </form>
      )}
      <div className="card">
        {!data?.length ? <p className="muted">{showArchived ? 'No archived organizations.' : 'No organizations yet.'}</p> : (<>
          <div className="field" style={{ maxWidth: 360 }}><label htmlFor="org-filter">Find</label>
            <input id="org-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Organization name" />
            {!showArchived && <ArchivedMatches query={filter} onRestored={() => void reload()} />}</div>
          <table>
            <thead><tr><th>Name</th><th>Participants</th><th>Portal</th>{editor && <th />}</tr></thead>
            <tbody>{shown!.map((d) => (
              <tr key={d.id} className="link" onClick={() => nav(`/organizations/${d.id}`)}>
                <td><Link to={`/organizations/${d.id}`} onClick={(e) => e.stopPropagation()}>{d.name}</Link></td>
                <td>{d.participants.toLocaleString()}</td>
                <td>{d.archivedAt ? <span className="muted small">archived {new Date(d.archivedAt).toLocaleDateString()}</span> : d.portalEnabled ? 'Enabled' : 'Off'}</td>
                {editor && <td>{d.archivedAt
                  ? admin && <button className="link" onClick={(e) => { e.stopPropagation(); void restore(d); }}>Restore</button>
                  : <button className="link" onClick={(e) => { e.stopPropagation(); void rename(d); }}>Rename</button>}</td>}</tr>))}
            </tbody>
          </table>
        </>)}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- one organization: all its participants
const PAGE = 200;
export function OrganizationDetail() {
  const { id } = useParams();
  const { me } = useAuth();
  const staff = me && isStaff(me.role);
  const nav = useNavigate();
  const orgs = useData<Department>(`/api/departments/${id}`);
  const org = orgs.data ?? undefined;
  const admin = !!me && isAdmin(me.role);
  const archive = async () => {
    if (!window.confirm(`Archive ${org?.name}? It disappears from lists and pick lists; an admin can restore it.`)) return;
    try { await post(`/api/departments/${id}/archive`); await orgs.reload(); } catch (x) { setErr(x); }
  };
  const restore = async () => { try { await post(`/api/departments/${id}/restore`); await orgs.reload(); } catch (x) { setErr(x); } };
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const [rows, setRows] = useState<ParticipantRow[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const load = async (offset: number) => {
    setLoading(true); setErr(null);
    try {
      const page = await get<ParticipantRow[]>(`/api/participants?departmentId=${id}&limit=${PAGE}&offset=${offset}${q ? `&q=${encodeURIComponent(q)}` : ''}`);
      setRows((prev) => (offset ? [...prev, ...page] : page));
      setMore(page.length === PAGE);
    } catch (x) { setErr(x); } finally { setLoading(false); }
  };
  useEffect(() => { void load(0); }, [id, q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {org && staff ? <CoverSheetTopBar back={<Link to="/organizations">← Organizations</Link>} query={`departmentId=${org.id}`} />
        : <p className="small"><Link to="/organizations">← Organizations</Link></p>}
      <h1>{org?.name ?? 'Organization'}</h1>
      {org && <p className="muted">{org.participants.toLocaleString()} participant{org.participants === 1 ? "" : "s"}{org.legacyPrefix ? ` · legacy ID prefix ${org.legacyPrefix}` : ''}</p>}
      <ErrorBox error={orgs.error ?? err} />
      {org?.archivedAt && <div className="alert info">Archived {new Date(org.archivedAt).toLocaleDateString()}{org.archivedReason ? ` (${org.archivedReason})` : ''}. It is hidden from lists and pick lists.
        {admin && <> <button className="link" onClick={() => void restore()}>Restore</button></>}</div>}
      {org && !org.archivedAt && admin && org.participants === 0 && <p className="small"><button className="link" onClick={() => void archive()}>Archive this organization</button> <span className="muted">(it has no participants)</span></p>}
      {org && <OrgDetailsCard org={org} onChange={() => void orgs.reload()} />}
      {org && !org.archivedAt && <PortalSettings org={org} onChange={() => void orgs.reload()} />}
      {org && staff && <OrgPacketCard orgId={org.id} />}
      {org && staff && <ScanPilesCard departmentId={org.id} />}
      <form className="card row" onSubmit={(e) => { e.preventDefault(); setQ(text.trim()); }}>
        <div><label htmlFor="org-q">Search this organization</label>
          <input id="org-q" value={text} onChange={(e) => setText(e.target.value)} placeholder="Name or employee ID" /></div>
        <div style={{ flex: '0 0 auto' }} className="actions"><button>Search</button>
          {q && <button type="button" className="secondary" onClick={() => { setText(''); setQ(''); }}>Clear</button>}</div>
      </form>
      <div className="card">
        {!rows.length ? <p className="muted">{loading ? 'Loading…' : 'No participants found.'}</p> : (<>
          <table>
            <thead><tr><th>Name</th><th>Employee ID</th><th>DOB</th><th>Sex</th><th>Last assessed</th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className={staff ? 'link' : ''} onClick={() => staff && nav(`/participants/${p.id}`)}>
                <td><strong>{p.lastName}, {p.firstName}</strong>{!p.active && <span className="badge" style={{ marginLeft: 6 }}>inactive</span>}</td>
                <td>{p.employeeId ?? '—'}</td><td>{p.dob ?? '—'}</td><td>{p.sex ?? '—'}</td>
                <td>{p.lastAssessedOn ? String(p.lastAssessedOn).slice(0, 10) : '—'}</td>
              </tr>))}
            </tbody>
          </table>
          <div className="spread" style={{ marginTop: '0.75rem' }}>
            <span className="muted small">Showing {rows.length.toLocaleString()}{q ? ' matching' : org ? ` of ${org.participants.toLocaleString()}` : ''}</span>
            {more && <button className="secondary" disabled={loading} onClick={() => void load(rows.length)}>{loading ? 'Loading…' : `Show ${PAGE} more`}</button>}
          </div>
        </>)}
      </div>
      {id && <DocumentsCard departmentId={id} />}
    </>
  );
}

// ---------------------------------------------------------------- organization portal settings
interface OrgPacketRow {
  id: string; status: 'queued' | 'running' | 'done' | 'failed'; includeRestricted: boolean; error: string | null; pageCount: number | null;
  people: number | null; createdAt: string; finishedAt: string | null; requestedBy: string | null; from: string; to: string;
  kind: 'range' | 'event_org' | 'event_all'; eventId: string | null; eventHost: string | null; eventLocation: string | null;
}
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const usDay = (s: string) => `${s.slice(5, 7)}/${s.slice(8, 10)}/${s.slice(0, 4)}`;
const PACKET_PRESETS: { label: string; range: () => [Date, Date] }[] = [
  { label: 'Today', range: () => [new Date(), new Date()] },
  { label: 'This month', range: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), n]; } },
  { label: 'Last month', range: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)]; } },
  { label: 'This year', range: () => { const n = new Date(); return [new Date(n.getFullYear(), 0, 1), n]; } },
  { label: 'Last year', range: () => { const y = new Date().getFullYear() - 1; return [new Date(y, 0, 1), new Date(y, 11, 31)]; } },
];

/** Report packet for this organization's signed visits on a date or between two dates, at any event (2026-10-22). */
function OrgPacketCard({ orgId }: { orgId: string }) {
  const { me } = useAuth();
  const nav = useNavigate();
  const { data, reload } = useData<OrgPacketRow[]>(`/api/departments/${orgId}/packets`);
  const today = isoDay(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState('');
  const working = data?.some((p) => p.status === 'queued' || p.status === 'running');
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [working, reload]);
  const bad = !from || !to || from > to;
  const make = async () => {
    setErr(null); setNote('');
    try {
      const r = await post<{ id: string; visits: number }>(`/api/departments/${orgId}/packets`, { from, to });
      setNote(`Making a packet of ${r.visits} signed visit${r.visits === 1 ? '' : 's'}. It appears below when ready.`);
      await reload();
    } catch (x) { setErr(x); }
  };
  const canOpenRestricted = !!me && canSeeRestricted(me.role);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Reports and packet</h2>
      <p className="muted small" style={{ marginTop: 0 }}>For this organization's signed visits on a date or between two dates, at any event. <b>Screening summary</b>: NGF’s letter to the
        department (group figures, averages and ranges, comparison with earlier years). <b>Executive summary</b>: counts, classes and the participant list.{' '}
        <b>Make packet (PDF)</b>: one PDF of
        the executive summary and participant list, then each person's report with their labs, EKGs, authorizations and outside records{canOpenRestricted ? ', and drug screens' : ''}.
        Visits not yet signed are listed as pending. Up to 300 reports per packet. The list below also shows packets made on this organization's events.</p>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '0 0 auto' }}><label htmlFor="pk-from">From</label>
          <input id="pk-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); if (e.target.value > to) setTo(e.target.value); }} /></div>
        <div className="field" style={{ flex: '0 0 auto' }}><label htmlFor="pk-to">To</label>
          <input id="pk-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
        <div className="actions" style={{ flex: '1 1 auto' }}>
          {PACKET_PRESETS.map((p) => <button key={p.label} type="button" className="secondary small"
            onClick={() => { const [a, b] = p.range(); setFrom(isoDay(a)); setTo(isoDay(b)); }}>{p.label}</button>)}
        </div>
      </div>
      <div className="actions" style={{ marginTop: '0.5rem' }}>
        <button type="button" className="secondary" disabled={bad} onClick={() => nav(`/organizations/${orgId}/screening-summary?from=${from}&to=${to}`)}>Screening summary</button>
        <button type="button" className="secondary" disabled={bad} onClick={() => nav(`/organizations/${orgId}/packet-summary?from=${from}&to=${to}`)}>Executive summary</button>
        <button type="button" disabled={bad} onClick={() => void make()}>Make packet (PDF)</button>
      </div>
      {note && <p className="small">{note}</p>}
      <ErrorBox error={err} />
      {!!data?.length && (
        <table style={{ marginTop: '0.5rem' }}>
          <thead><tr><th>Dates</th><th>Packet</th><th>Made</th><th>By</th><th>People</th><th>Pages</th><th>Status</th><th /></tr></thead>
          <tbody>{data.map((p) => {
            const pdf = p.eventId ? `/api/events/${p.eventId}/packets/${p.id}/pdf` : `/api/departments/${orgId}/packets/${p.id}/pdf`;
            return (
            <tr key={p.id}>
              <td>{p.from === p.to ? usDay(p.from) : `${usDay(p.from)} – ${usDay(p.to)}`}</td>
              <td className="small">{p.kind === 'range' ? 'Date range' : <>
                <Link to={`/events/${p.eventId}`}>Event</Link>{p.kind === 'event_all' ? ' (everyone at it)' : ' (this organization)'}
                {p.eventHost && p.kind === 'event_all' && <div className="muted">Host: {p.eventHost}</div>}</>}</td>
              <td className="small">{new Date(p.finishedAt ?? p.createdAt).toLocaleString()}</td>
              <td className="small">{p.requestedBy ?? '—'}{p.includeRestricted && <span className="badge provisional" style={{ marginLeft: 6 }}>incl. drug screens</span>}</td>
              <td>{p.people ?? '—'}</td><td>{p.pageCount ?? '—'}</td>
              <td>{p.status === 'done' ? <span className="badge released">ready</span>
                : p.status === 'failed' ? <span className="badge void" title={p.error ?? ''}>failed</span>
                : <span className="badge reviewed">{p.status === 'running' ? 'building…' : 'waiting…'}</span>}
                {p.status === 'failed' && p.error && <div className="muted small">{p.error}</div>}</td>
              <td>{p.status === 'done' && (!p.includeRestricted || canOpenRestricted) && <>
                <a href={pdf} target="_blank" rel="noopener">Open</a>{' · '}
                <a href={`${pdf}?download=1`}>Download</a></>}</td>
            </tr>);
          })}</tbody>
        </table>)}
    </div>
  );
}

function PortalSettings({ org, onChange }: { org: Department; onChange: () => void }) {
  const { me } = useAuth();
  const admin = !!me && isAdmin(me.role);
  const users = useData<UserRow[]>(admin ? '/api/admin/users' : null);
  const [err, setErr] = useState<unknown>(null);
  const contacts = users.data?.filter((u) => u.role === 'department_contact' && u.departmentIds.includes(org.id)) ?? [];
  const set = async (field: 'portalEnabled' | 'portalPackets', value: boolean) => {
    const msg = field === 'portalEnabled'
      ? (value ? `Switch on the portal for ${org.name}? Its contacts will see roster names and status.` : `Switch off the portal for ${org.name}? Its contacts lose access immediately.`)
      : (value ? `Share released report packets (PDF) with ${org.name}'s contacts?\n\nOnly do this once NGF has the participants' authorization for this organization.`
        : `Stop sharing report packets with ${org.name}?`);
    if (!window.confirm(msg)) return;
    setErr(null);
    try { await patch(`/api/departments/${org.id}`, { [field]: value }); onChange(); } catch (x) { setErr(x); }
  };
  return (
    <div className="card">
      <div className="spread">
        <h2 style={{ margin: 0 }}>Organization portal</h2>
        <Link className="btn secondary" to={`/portal/${org.id}`}>Preview portal</Link>
      </div>
      <ErrorBox error={err} />
      <div className="portal-settings">
        <div><strong>Portal</strong> <span className={`badge ${org.portalEnabled ? 'signed' : ''}`}>{org.portalEnabled ? 'On' : 'Off'}</span>
          <div className="muted small">Contacts see roster names, status (complete / in progress / due), cleared or referred after release, and testing dates.</div>
          {admin && <button className="link" onClick={() => void set('portalEnabled', !org.portalEnabled)}>{org.portalEnabled ? 'Switch off' : 'Switch on'}</button>}</div>
        <div><strong>Report packets</strong> <span className={`badge ${org.portalPackets ? 'signed' : ''}`}>{org.portalPackets ? 'Shared' : 'Not shared'}</span>
          <div className="muted small">When shared, contacts can download each person's latest released report (PDF). Needs the participants' authorization.</div>
          {admin && <button className="link" onClick={() => void set('portalPackets', !org.portalPackets)}>{org.portalPackets ? 'Stop sharing' : 'Share packets'}</button>}</div>
        {admin && <div><strong>Contacts</strong>
          {!contacts.length ? <div className="muted small">No contacts yet.</div>
            : <ul className="small" style={{ margin: '0.25rem 0' }}>{contacts.map((c) => <li key={c.id}>{c.displayName} · {c.email}{c.disabledAt ? ' (disabled)' : ''}</li>)}</ul>}
          <Link className="small" to="/admin/users">Add a contact on the Users page</Link> <span className="muted small">(role: Organization contact)</span></div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- users
interface UserRow {
  id: string; email: string; displayName: string; role: Role; credential: string | null; mfaEnrolled: boolean;
  mustChangePassword: boolean; disabledAt: string | null; lastLoginAt: string | null; locked: boolean; departmentIds: string[];
  itSupport?: boolean;   // ILH IT Support: cannot be disabled or re-roled here
}

// Plain-language summary of what each role can do. Mirrors the checks enforced by the API (requireUser role
// lists in apps/api/src/routes) and the database row-level security — keep the two in step.
const ROLE_SUMMARY: Record<Role, string> = {
  technician: 'NGF field and office staff. Enters and edits assessments, runs events and rosters, views reports and drug screens, releases signed packets, uses Imports (incl. paper signatures), adds and renames organizations. Cannot sign.',
  physician: 'Everything a technician can do, plus signs assessments from the sign queue, releases them, and sees restricted documents (drug screens).',
  ngf_admin: 'NGF office management. All staff access plus users, organizations, scanned-form imports, the audit log and releasing signed packets. Cannot sign — only a physician can.',
  department_contact: 'A client organization\'s point of contact (chief, HR). Uses the organization portal: roster status, cleared / referred after release, testing dates, and sends in rosters. Released report PDFs only if NGF shares them. No test results.',
  platform_admin: 'ILH Tech Services break-glass support account. Same access as an NGF admin; every use is logged. Not assignable here.',
};

type Access = 'yes' | 'no' | 'own';
const ACCESS_ROWS: [string, Access, Access, Access, Access][] = [
  // capability                                        tech   phys   admin  dept
  ['Participants — search, add, edit',                  'yes', 'yes', 'yes', 'no'],
  ['Roster names and completion status',                'yes', 'yes', 'yes', 'own'],
  ['Cleared / referred (after release)',                'yes', 'yes', 'yes', 'own'],
  ['Released report PDFs',                              'yes', 'yes', 'yes', 'own'],
  ['Rosters (contacts send in, NGF imports)',           'yes', 'yes', 'yes', 'own'],
  ['Events — create, import roster CSV, check in',      'yes', 'yes', 'yes', 'no'],
  ['Assessments — enter and edit drafts',               'yes', 'yes', 'yes', 'no'],
  ['Clinical results, history and reports',             'yes', 'yes', 'yes', 'no'],
  ['Mark reviewed / start a correction',                'yes', 'yes', 'yes', 'no'],
  ['Sign assessments (e-signature)',                    'no',  'yes', 'no',  'no'],
  ['Release signed packets',                            'yes', 'yes', 'yes', 'no'],
  ['Restricted documents (drug screens)',               'yes', 'yes', 'yes', 'no'],
  ['Imports — upload scanned forms, review, create',    'yes', 'no',  'yes', 'no'],
  ['Record paper signatures (paper-signed imports)',    'yes', 'no',  'yes', 'no'],
  ['Organizations — add, rename',                       'yes', 'no',  'yes', 'no'],
  ['Users — add, reset, disable',                       'no',  'no',  'yes', 'no'],
  ['Audit log',                                         'no',  'no',  'yes', 'no'],
];
const ACCESS_TEXT: Record<Access, string> = { yes: '✓', no: '—', own: 'Own org' };

function RolesGuide() {
  return (
    <details className="card roles-guide">
      <summary><h2>Roles and access</h2></summary>
      <dl className="role-defs">
        {(['technician', 'physician', 'ngf_admin', 'department_contact', 'platform_admin'] as Role[]).map((r) => (
          <div key={r}><dt>{ROLE_LABEL[r]}</dt><dd>{ROLE_SUMMARY[r]}</dd></div>
        ))}
      </dl>
      <div className="table-scroll">
        <table className="access">
          <thead><tr><th>Can…</th><th>Technician</th><th>Physician</th><th>NGF admin</th><th>Org contact</th></tr></thead>
          <tbody>{ACCESS_ROWS.map(([what, ...cells]) => (
            <tr key={what}><td>{what}</td>{cells.map((c, i) => <td key={i} className={`acc ${c}`}>{ACCESS_TEXT[c]}</td>)}</tr>
          ))}</tbody>
        </table>
      </div>
      <p className="muted small">
        Everyone signs in with their own password and an authenticator app (MFA), and must change the one-time password at first
        sign-in. Sessions end after 30 minutes idle or 12 hours. Five wrong passwords in a row locks the account for 15 minutes (an admin can reset it sooner).
        Every read or change of participant data is written to the audit log. Signed and released records cannot be edited — a
        correction creates a new version that must be signed again.
      </p>
    </details>
  );
}

export function Users() {
  const { data, error, reload } = useData<UserRow[]>('/api/admin/users');
  const depts = useData<Department[]>('/api/departments');
  const [form, setForm] = useState({ email: '', displayName: '', role: 'technician' as Role, credential: '', departmentId: '' });
  const [issued, setIssued] = useState<{ email: string; tempPassword: string } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  // Disabled users stay out of the list unless asked for.
  const [showDisabled, setShowDisabled] = useState(false);
  const disabledCount = data?.filter((u) => u.disabledAt).length ?? 0;
  const users = data?.filter((u) => showDisabled || !u.disabledAt);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      const r = await post<{ tempPassword: string }>('/api/admin/users', {
        email: form.email, displayName: form.displayName, role: form.role, credential: form.credential || null,
        departmentIds: form.role === 'department_contact' && form.departmentId ? [form.departmentId] : [],
      });
      setIssued({ email: form.email, tempPassword: r.tempPassword });
      setForm({ email: '', displayName: '', role: 'technician', credential: '', departmentId: '' });
      await reload();
    } catch (x) { setErr(x); }
  };
  const reset = async (u: UserRow) => {
    if (!window.confirm(`Reset ${u.displayName}? This issues a new one-time password, clears their MFA, and signs them out everywhere.`)) return;
    try {
      const r = await post<{ tempPassword: string }>(`/api/admin/users/${u.id}/reset`);
      setIssued({ email: u.email, tempPassword: r.tempPassword });
      await reload();
    } catch (x) { setErr(x); }
  };
  const toggle = async (u: UserRow) => {
    await patch(`/api/admin/users/${u.id}`, { disabled: !u.disabledAt }).catch(setErr);
    await reload();
  };
  const deptName = (id: string) => depts.data?.find((d) => d.id === id)?.name ?? id;
  // Change a user's role (not your own). They are signed out so the new role applies at their next sign-in.
  const { me } = useAuth();
  const [editing, setEditing] = useState<{ id: string; role: Role; credential: string; departmentId: string } | null>(null);
  const saveRole = async (u: UserRow) => {
    if (!editing) return;
    if (editing.role === u.role && (editing.role !== 'physician' || editing.credential === (u.credential ?? ''))
      && (editing.role !== 'department_contact' || editing.departmentId === (u.departmentIds[0] ?? ''))) { setEditing(null); return; }
    if (editing.role === 'department_contact' && !editing.departmentId) { setErr(new Error('Choose the organization this contact belongs to.')); return; }
    if (editing.role !== u.role && !window.confirm(`Change ${u.displayName} from ${ROLE_LABEL[u.role]} to ${ROLE_LABEL[editing.role]}? They will be signed out and get the new access at their next sign-in.`)) return;
    setErr(null);
    try {
      await patch(`/api/admin/users/${u.id}`, {
        role: editing.role,
        credential: editing.role === 'physician' ? (editing.credential.trim() || null) : undefined,
        departmentIds: editing.role === 'department_contact' ? [editing.departmentId] : undefined,
      });
      setEditing(null);
      await reload();
    } catch (x) { setErr(x); }
  };

  return (
    <>
      <h1>Users</h1>
      <ErrorBox error={error ?? err} />
      {issued && (
        <div className="alert ok">
          One-time password for <strong>{issued.email}</strong>: <span className="mono" style={{ fontSize: '1.1rem' }}>{issued.tempPassword}</span>
          <div className="small">Give it to them in person or by phone — it is shown only once. They will choose their own password and set up MFA at first sign-in.</div>
          <button className="link" onClick={() => setIssued(null)}>Dismiss</button>
        </div>
      )}
      <form className="card" onSubmit={create}>
        <h2>Add a user</h2>
        <div className="row">
          <div className="field"><label>Name</label><input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required /></div>
          <div className="field"><label>Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div className="field"><label>Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {(['technician', 'physician', 'ngf_admin', 'department_contact'] as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select></div>
          {form.role === 'physician' && <div className="field"><label>Credential</label><input value={form.credential} onChange={(e) => setForm({ ...form, credential: e.target.value })} placeholder="MD, DO…" /></div>}
          {form.role === 'department_contact' && <div className="field"><label>Organization</label>
            <select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} required>
              <option value="">Choose…</option>{depts.data?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></div>}
        </div>
        <p className="hint" style={{ margin: '0 0 0.75rem' }}><strong>{ROLE_LABEL[form.role]}:</strong> {ROLE_SUMMARY[form.role]}</p>
        <button>Create user</button>
      </form>
      <RolesGuide />
      <div className="card">
        <div className="spread small" style={{ marginBottom: '0.5rem' }}>
          <span className="muted">{users?.length ?? 0} user{users?.length === 1 ? '' : 's'}{!showDisabled && disabledCount ? ` (${disabledCount} disabled hidden)` : ''}</span>
          <label><input type="checkbox" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} /> Show disabled users</label>
        </div>
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last sign-in</th><th /></tr></thead>
          <tbody>{users?.map((u) => (
            <tr key={u.id}>
              <td>{u.displayName}{u.credential ? `, ${u.credential}` : ''}
                {u.itSupport && <span className="badge it-support" title="ILH Tech Services support account: cannot be disabled or have its role changed here" style={{ marginLeft: 6 }}>IT Support</span>}
                {u.departmentIds.length > 0 && <div className="muted small">{u.departmentIds.map(deptName).join(', ')}</div>}</td>
              <td>{u.email}</td>
              <td>{editing?.id === u.id ? (
                <div className="role-edit">
                  <select aria-label="Role" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value as Role })}>
                    {(['technician', 'physician', 'ngf_admin', 'department_contact'] as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                  {editing.role === 'physician' && <input aria-label="Credential" placeholder="MD, DO…" value={editing.credential} onChange={(e) => setEditing({ ...editing, credential: e.target.value })} />}
                  {editing.role === 'department_contact' && (
                    <select aria-label="Organization" value={editing.departmentId} onChange={(e) => setEditing({ ...editing, departmentId: e.target.value })}>
                      <option value="">Organization…</option>
                      {depts.data?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>)}
                  <div className="muted small">{ROLE_SUMMARY[editing.role]}</div>
                  <div className="actions"><button onClick={() => void saveRole(u)}>Save</button><button className="secondary" onClick={() => setEditing(null)}>Cancel</button></div>
                </div>
              ) : ROLE_LABEL[u.role]}</td>
              <td>{u.disabledAt ? <span className="badge">disabled</span> : u.locked ? <span className="badge provisional">locked</span>
                : u.mustChangePassword ? <span className="badge draft">invited</span> : !u.mfaEnrolled ? <span className="badge draft">no MFA</span>
                : <span className="badge signed">active</span>}</td>
              <td className="small">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
              <td className="actions">{u.id !== me?.id && u.role !== 'platform_admin' && !u.itSupport && !editing && (
                <button className="link" onClick={() => setEditing({ id: u.id, role: u.role, credential: u.credential ?? '', departmentId: u.departmentIds[0] ?? '' })}>Change role</button>)}
                <button className="link" onClick={() => void reset(u)}>Reset</button>
                {!(u.itSupport && !u.disabledAt) && u.id !== me?.id && <button className="link" onClick={() => void toggle(u)}>{u.disabledAt ? 'Enable' : 'Disable'}</button>}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- audit
interface AuditRow { id: number; at: string; action: string; entity: string | null; entityId: string | null; actorRole: string;
  actorName: string | null; ip: string | null; details: Record<string, unknown> }

const AUDIT_PAGE = 100;

export function Audit() {
  const [entity, setEntity] = useState('');
  const [page, setPage] = useState(0);
  // One extra row tells us whether an older page exists.
  const { data: raw, error } = useData<AuditRow[]>(`/api/admin/audit?limit=${AUDIT_PAGE + 1}&offset=${page * AUDIT_PAGE}${entity ? `&entity=${entity}` : ''}`);
  const data = raw?.slice(0, AUDIT_PAGE);
  const older = (raw?.length ?? 0) > AUDIT_PAGE;
  const pager = (
    <div className="spread small" style={{ margin: '0.25rem 0 0.75rem' }}>
      <span className="muted">{data?.length ? `Entries ${(page * AUDIT_PAGE + 1).toLocaleString()}–${(page * AUDIT_PAGE + data.length).toLocaleString()}, newest first` : ''}</span>
      <div className="actions">
        <button className="secondary" disabled={page === 0} onClick={() => setPage(0)}>Newest</button>
        <button className="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>← Newer</button>
        <span style={{ alignSelf: 'center' }}>Page {page + 1}</span>
        <button className="secondary" disabled={!older} onClick={() => setPage(page + 1)}>Older →</button>
      </div>
    </div>
  );
  return (
    <>
      <h1>Audit log</h1>
      <p className="muted small">Every sign-in and every read or change of participant data is recorded here. Entries cannot be edited or deleted.</p>
      <div className="card row">
        <div style={{ flex: '0 1 260px' }}><label>Filter</label>
          <select value={entity} onChange={(e) => { setEntity(e.target.value); setPage(0); }}>
            <option value="">Everything</option><option value="user">Users / sign-in</option>
            <option value="participant">Participants</option><option value="assessment">Assessments</option>
            <option value="department">Organizations</option>
          </select></div>
      </div>
      <ErrorBox error={error} />
      <div className="card">
        {pager}
        <table>
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Record</th><th>IP</th><th>Details</th></tr></thead>
          <tbody>{data?.map((a) => (
            <tr key={a.id}><td className="small">{new Date(a.at).toLocaleString()}</td>
              <td>{a.actorName ?? <span className="muted">{a.actorRole}</span>}</td><td className="mono small">{a.action}</td>
              <td className="mono small">{a.entity ? `${a.entity}${a.entityId ? ` ${a.entityId.slice(0, 8)}` : ''}` : '—'}</td>
              <td className="small">{a.ip ?? '—'}</td>
              <td className="mono small">{Object.keys(a.details).length ? JSON.stringify(a.details) : ''}</td></tr>))}</tbody>
        </table>
        {(data?.length ?? 0) > 20 && pager}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- document titles (standard list + learned from filing)
interface TitleRow { id: string; docType: string; title: string; sort: number; active: boolean }
interface LearnedRow { key: string; heading: string; docType: string; title: string; uses: number; updatedAt: string; updatedBy: string | null }
const TYPE_LABEL: Record<string, string> = {
  lab_panel: 'Lab results', drug_screen: 'Drug screen', ekg: 'EKG', pft: 'Spirometry / PFT', clearance: 'Clearance form',
  test_result: 'Other test result (add a title for each new kind of test)', authorization: 'Authorization / consent', medical_history: 'Medical history',
  prior_report: 'Prior report', outside_record: 'Outside record', roster: 'Roster / sign-in sheet', correspondence: 'Letter / correspondence', other: 'Other',
};

export function DocumentTitles() {
  const { data, error, reload } = useData<{ titles: TitleRow[]; learned: LearnedRow[] }>('/api/document-titles');
  const [adding, setAdding] = useState<Record<string, string>>({});
  const [err, setErr] = useState<unknown>(null);
  const add = async (docType: string) => {
    const title = (adding[docType] ?? '').trim();
    if (!title) return;
    setErr(null);
    try { await post('/api/document-titles', { docType, title }); setAdding({ ...adding, [docType]: '' }); await reload(); } catch (x) { setErr(x); }
  };
  const toggle = async (t: TitleRow) => { setErr(null); try { await patch(`/api/document-titles/${t.id}`, { active: !t.active }); await reload(); } catch (x) { setErr(x); } };
  const forget = async (l: LearnedRow) => {
    if (!window.confirm(`Forget "${l.heading}" -> ${l.title}? The next page with that heading gets the standard suggestion again.`)) return;
    setErr(null); try { await post('/api/document-titles/learned/forget', { key: l.key }); await reload(); } catch (x) { setErr(x); }
  };
  return (
    <>
      <h1>Document titles</h1>
      <p className="muted">When a scanned page is filed, its title is picked from this list for its type. Whatever is chosen for a form is remembered by
        the heading printed on it, so the next page with the same heading gets the same type and title.</p>
      <ErrorBox error={error ?? err} />
      <div className="card">
        <h2>Standard titles</h2>
        <table><tbody>{Object.keys(TYPE_LABEL).map((type) => (
          <tr key={type}>
            <td style={{ width: '22%' }}><b>{TYPE_LABEL[type]}</b></td>
            <td>
              <div className="chips">{(data?.titles ?? []).filter((t) => t.docType === type).map((t) => (
                <span key={t.id} className={`chip${t.active ? '' : ' chip-off'}`} title={t.active ? 'Click to stop offering this title' : 'Not offered; click to offer again'}
                  onClick={() => void toggle(t)}>{t.title}{t.active ? '' : ' (off)'}</span>))}</div>
              <div className="row" style={{ marginTop: 6, alignItems: 'end' }}>
                <div className="field" style={{ marginBottom: 0 }}><input aria-label={`New title for ${TYPE_LABEL[type]}`} placeholder="Add a title"
                  value={adding[type] ?? ''} onChange={(e) => setAdding({ ...adding, [type]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(type); } }} /></div>
                <div style={{ flex: '0 0 auto' }}><button className="secondary" disabled={!(adding[type] ?? '').trim()} onClick={() => void add(type)}>Add</button></div>
              </div>
            </td>
          </tr>))}</tbody></table>
      </div>
      <div className="card">
        <h2>Learned from filing</h2>
        {!data?.learned.length ? <p className="muted">Nothing yet: it fills in as pages are filed.</p> : (
          <table>
            <thead><tr><th>Heading on the page</th><th>Filed as</th><th>Times</th><th>Last</th><th /></tr></thead>
            <tbody>{data.learned.map((l) => (
              <tr key={l.key}>
                <td className="small">{l.heading}</td><td>{TYPE_LABEL[l.docType] ?? l.docType}: <b>{l.title}</b></td><td>{l.uses}</td>
                <td className="small">{new Date(l.updatedAt).toLocaleDateString()}<div className="muted">{l.updatedBy ?? ''}</div></td>
                <td className="actions"><button className="link" onClick={() => void forget(l)}>Forget</button></td>
              </tr>))}</tbody>
          </table>)}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- possible duplicate people (admins)
interface DupPerson { id: string; firstName: string; lastName: string; employeeId: string | null; departmentName: string | null; legacy: boolean; assessments: number; lastAssessedOn: string | null }
interface DupGroup { f: string; l: string; dob: string; n: number; people: DupPerson[] }

export function DuplicatePeople() {
  const { data, error, reload } = useData<{ total: number; groups: DupGroup[] }>('/api/participants/duplicates');
  const [keep, setKeep] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const key = (g: DupGroup) => `${g.f}|${g.l}|${g.dob}`;
  const merge = async (g: DupGroup) => {
    const into = keep[key(g)] ?? g.people[0]!.id;
    const kept = g.people.find((p) => p.id === into)!;
    const others = g.people.filter((p) => p.id !== into);
    if (!window.confirm(`Keep ${kept.lastName}, ${kept.firstName} (ID ${kept.employeeId ?? '—'}) and merge ${others.length} other record(s) into it?\n\nTheir assessments, documents and organizations move to the kept record. It can be undone.`)) return;
    setErr(null); setBusy(key(g));
    try {
      for (const o of others) await post(`/api/participants/${o.id}/merge`, { intoId: into, reason: 'Same person: same name and date of birth (Possible duplicates)' });
      setDone(`${kept.firstName} ${kept.lastName}: ${others.length} record(s) merged.`);
      await reload();
    } catch (x) { setErr(x); } finally { setBusy(null); }
  };
  return (
    <>
      <h1>Possible duplicate people</h1>
      <p className="muted">Records with the same first and last name and date of birth, usually the same person imported twice from ePhysioLogic
        under two ID numbers. Check that they really are one person (ID numbers, organizations, test dates), choose the record to keep (the one
        with the most assessments is chosen), then <b>Merge</b>. Everything moves to the kept record; the others are retired. Merges can be undone.</p>
      <ErrorBox error={error ?? err} />
      {done && <div className="alert ok">{done}</div>}
      {!data ? <p className="muted">Loading…</p> : <>
        <p className="small muted">{data.total} group{data.total === 1 ? '' : 's'}{data.groups.length < data.total ? ` (showing the first ${data.groups.length})` : ''}.</p>
        {data.groups.map((g) => (
          <div key={key(g)} className="card">
            <div className="spread"><b>{g.people[0]!.lastName}, {g.people[0]!.firstName} · DOB {g.dob}</b>
              <button disabled={busy === key(g)} onClick={() => void merge(g)}>{busy === key(g) ? 'Merging…' : 'Merge'}</button></div>
            {g.people.map((p, i) => (
              <label key={p.id} className="small" style={{ display: 'block' }}>
                <input type="radio" name={key(g)} checked={(keep[key(g)] ?? g.people[0]!.id) === p.id} onChange={() => setKeep({ ...keep, [key(g)]: p.id })} />
                {' '}<Link to={`/participants/${p.id}`}>{p.lastName}, {p.firstName}</Link> · ID {p.employeeId ?? '—'} · {p.departmentName ?? 'No organization'}
                {' '}· {p.assessments} assessment{p.assessments === 1 ? '' : 's'}{p.lastAssessedOn ? `, last ${p.lastAssessedOn}` : ''}{p.legacy ? ' · from ePhysioLogic' : ''}
                {i === 0 && <span className="muted"> · most assessments</span>}
              </label>))}
          </div>))}
      </>}
    </>
  );
}

// ---------------------------------------------------------------- organization details (from the cleanup lookups; admins edit)
const DETAIL_FIELDS: [keyof OrgDetails, string, number][] = [
  ['category', 'Category', 60], ['state', 'State', 2], ['county', 'County', 60], ['street', 'Street address', 200], ['city', 'City', 80],
  ['zip', 'Zip', 10], ['phone', 'Phone', 40], ['website', 'Website', 300],
];
const CATEGORIES = ['Fire', 'Police', 'Gov', 'School', 'College/University', 'Healthcare', 'Private/Industry', 'Unknown'];

function OrgDetailsCard({ org, onChange }: { org: Department; onChange: () => void }) {
  const { me } = useAuth();
  const admin = !!me && isAdmin(me.role);
  const d = org.details;
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const [err, setErr] = useState<unknown>(null);
  if (!d) return null;
  const save = async () => {
    setErr(null);
    try { await api('PUT', `/api/departments/${org.id}/details`, edit); setEdit(null); onChange(); } catch (x) { setErr(x); }
  };
  const address = [d.street, [d.city, d.state].filter(Boolean).join(', '), d.zip].filter(Boolean).join(' · ');
  return (
    <div className="card">
      <div className="spread"><h2 style={{ margin: 0 }}>Details</h2>
        {admin && !edit && <button className="secondary" onClick={() => setEdit(Object.fromEntries(DETAIL_FIELDS.map(([k]) => [k, (d[k] as string | null) ?? ''])))}>Edit</button>}</div>
      <ErrorBox error={err} />
      {!edit ? (
        <table className="small" style={{ marginTop: 6 }}><tbody>
          <tr><td style={{ width: '22%' }}>Category</td><td>{d.category ?? '—'}</td></tr>
          <tr><td>County / state</td><td>{[d.county, d.state].filter(Boolean).join(', ') || '—'}</td></tr>
          <tr><td>Address</td><td>{address || '—'}</td></tr>
          <tr><td>Phone</td><td>{d.phone ?? '—'}</td></tr>
          <tr><td>Website</td><td>{d.website ? <a href={d.website} target="_blank" rel="noopener noreferrer">{d.website}</a> : '—'}</td></tr>
          {(d.lookupSource || d.lookupConfidence) && <tr><td>Source</td><td className="muted">{[d.lookupConfidence, d.lookupSource].filter(Boolean).join(' · ')}</td></tr>}
        </tbody></table>
      ) : (<>
        <div className="row" style={{ flexWrap: 'wrap' }}>{DETAIL_FIELDS.map(([k, label, max]) => (
          <div key={k} className="field" style={{ flex: k === 'street' || k === 'website' ? '1 1 100%' : '1 1 30%' }}><label>{label}
            {k === 'category'
              ? <select value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}><option value="">—</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              : <input value={edit[k]} maxLength={max} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />}</label></div>))}</div>
        <div className="actions"><button onClick={() => void save()}>Save</button><button className="secondary" onClick={() => setEdit(null)}>Cancel</button></div>
      </>)}
    </div>
  );
}

// ---------------------------------------------------------------- physicians
interface PhysicianRow {
  id: string; fullName: string; title: string | null; npi: string | null; scLicense: string | null; licenseState: string; notes: string | null; specialty: string | null;
  licenseExpires: string | null; active: boolean; expired: boolean; label: string;
}
const blankDoc = { fullName: '', title: 'MD', npi: '', scLicense: '', licenseState: 'SC', notes: '', specialty: '', licenseExpires: '' };

/** NGF's physicians (admins): paper signatures are chosen from this list, and scanned signatures are matched to it. */
export function Physicians() {
  const { data, error, reload } = useData<PhysicianRow[]>('/api/physicians?all=1');
  const [edit, setEdit] = useState<string | null>(null);   // id, or 'new'
  const [sigs, setSigs] = useState<string | null>(null);   // physician whose reference signatures are shown
  const [f, setF] = useState(blankDoc);
  const [err, setErr] = useState<unknown>(null);
  const open = (p: PhysicianRow | null) => {
    setErr(null);
    setEdit(p ? p.id : 'new');
    setF(p ? { fullName: p.fullName, title: p.title ?? '', npi: p.npi ?? '', scLicense: p.scLicense ?? '', licenseState: p.licenseState, notes: p.notes ?? '', specialty: p.specialty ?? '', licenseExpires: p.licenseExpires ?? '' } : blankDoc);
  };
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    const body = { fullName: f.fullName.trim(), title: f.title.trim() || null, npi: f.npi.trim() || null, scLicense: f.scLicense.trim() || null, licenseState: f.licenseState.trim().toUpperCase() || 'SC', notes: f.notes.trim() || null,
      specialty: f.specialty.trim() || null, licenseExpires: f.licenseExpires || null };
    try {
      if (edit === 'new') await post('/api/physicians', body); else await patch(`/api/physicians/${edit}`, body);
      setEdit(null); await reload();
    } catch (x) { setErr(x); }
  };
  const setActive = async (p: PhysicianRow, active: boolean) => {
    setErr(null);
    try { await patch(`/api/physicians/${p.id}`, { active }); await reload(); } catch (x) { setErr(x); }
  };
  const set = (k: keyof typeof blankDoc) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <div className="spread"><h1>Physicians</h1>{!edit && <button onClick={() => open(null)}>Add physician</button>}</div>
      <p className="muted small">Paper signatures on imports are chosen from this list, and the signature on each scanned page is matched to it.
        An expired license is flagged when someone picks that physician (not blocked).</p>
      <ErrorBox error={error ?? err} />
      {edit && (
        <form className="card" onSubmit={save}>
          <h2 style={{ marginTop: 0 }}>{edit === 'new' ? 'Add physician' : 'Edit physician'}</h2>
          <div className="row">
            <div className="field" style={{ flex: '2 1 260px' }}><label>Full name<input value={f.fullName} onChange={set('fullName')} required placeholder="e.g. John Keith McKelvey" /></label></div>
            <div className="field" style={{ flex: '0 1 90px' }}><label>Title<input value={f.title} onChange={set('title')} placeholder="MD" /></label></div>
          </div>
          <div className="row">
            <div className="field"><label>NPI<input value={f.npi} onChange={set('npi')} inputMode="numeric" maxLength={10} /></label></div>
            <div className="field"><label>License #<input value={f.scLicense} onChange={set('scLicense')} /></label></div>
            <div className="field" style={{ flex: '0 0 5rem' }}><label>State<input value={f.licenseState} maxLength={2} onChange={set('licenseState')} /></label></div>
            <div className="field"><label>License expires<input type="date" value={f.licenseExpires} onChange={set('licenseExpires')} /></label></div>
          </div>
          <div className="field"><label>Specialty (board)<input value={f.specialty} onChange={set('specialty')} /></label></div>
          <div className="field"><label>Note (shown beside the name when choosing, e.g. "Frankfort FD only")<input value={f.notes} maxLength={200} onChange={set('notes')} /></label></div>
          <div className="actions"><button>Save</button><button type="button" className="secondary" onClick={() => setEdit(null)}>Cancel</button></div>
        </form>)}
      <div className="card">
        {!data ? <p className="muted">Loading…</p> : (
          <table><thead><tr><th>Physician</th><th>NPI</th><th>License</th><th>Specialty</th><th>Expires</th><th /></tr></thead>
            <tbody>{data.map((p) => (<Fragment key={p.id}>
              <tr className={p.active ? '' : 'muted'}>
                <td><b>{p.label}</b>{!p.active && <span className="badge void" style={{ marginLeft: 6 }}>inactive</span>}{p.notes && <div className="small muted">{p.notes}</div>}</td>
                <td className="mono">{p.npi ?? '—'}</td><td>{p.scLicense ? `${p.licenseState} ${p.scLicense}` : '—'}</td><td className="small">{p.specialty ?? '—'}</td>
                <td>{p.licenseExpires ?? '—'}{p.expired && <span className="badge void" style={{ marginLeft: 6 }}>expired</span>}</td>
                <td className="actions small">
                  <button className="link" onClick={() => open(p)}>Edit</button>
                  <button className="link" onClick={() => setSigs(sigs === p.id ? null : p.id)}>Signatures</button>
                  <button className="link" onClick={() => void setActive(p, !p.active)}>{p.active ? 'Deactivate' : 'Reactivate'}</button></td>
              </tr>
              {sigs === p.id && <tr><td colSpan={6}><SignatureRefs physicianId={p.id} name={p.label} /></td></tr>}
              </Fragment>))}</tbody></table>)}
      </div>
    </>
  );
}

/** A physician's verified example signatures (cut from confirmed paper pages); the reader compares new pages with the latest. */
function SignatureRefs({ physicianId, name }: { physicianId: string; name: string }) {
  const { data, error, reload } = useData<{ id: string; status: string; error: string | null; readyAt: string | null; batchId: string; pageNo: number }[]>(
    `/api/physicians/${physicianId}/signatures`);
  const [err, setErr] = useState<unknown>(null);
  const remove = async (id: string) => {
    if (!window.confirm(`Remove this example from ${name}'s reference signatures? (Use it when the wrong area was cut out or it is not their signature.)`)) return;
    setErr(null);
    try { await post(`/api/physicians/signatures/${id}/remove`); await reload(); } catch (x) { setErr(x); }
  };
  return (
    <div className="sig-refs">
      <p className="small muted" style={{ margin: '0 0 0.5rem' }}>Verified examples of {name}'s signature, cut from paper pages a reviewer confirmed. The newest
        three are shown to the reader with every scanned page, to suggest who signed. Each newly confirmed page adds one.</p>
      <ErrorBox error={error ?? err} />
      {!data ? <p className="muted small">Loading…</p> : !data.length ? <p className="muted small">None yet: they appear as paper-signed pages are confirmed on Imports.</p> : (
        <div className="sig-grid">{data.map((r) => (
          <div key={r.id} className="sig-card">
            {r.status === 'ready' ? <img src={`/api/physicians/signatures/${r.id}/image`} alt={`${name} signature example`} />
              : <div className="muted small">{r.status === 'pending' ? 'Being cut out…' : `Could not cut out: ${r.error ?? 'error'}`}</div>}
            <div className="small spread"><Link to={`/imports/${r.batchId}`}>page {r.pageNo}</Link>
              <button className="link" onClick={() => void remove(r.id)}>Remove</button></div>
          </div>))}</div>)}
    </div>);
}
